<template>
  <div class="tr__container">
    <div class="tr">
      <div class="tr__background"></div>
      <div class="tr__title">
        Add some reviewers
      </div>
      <template v-if="ui.load.loading">
        <div class="d-flex flex-column justify-content-center align-items-center my-3">
          <loading-spinner size="2x"
                           class="m-4"></loading-spinner>
          <p>Loading reviewers, please wait...</p>
        </div>
      </template>
      <template v-else>
        <transition-group appear
                          name="fade">
          <div
            v-for="(value, index) in reviewers"
            :key="`key:${index}`"
            class="tr__reviewers__reviewer">
            <div class="tr__reviewers__reviewer__title">
              Reviewer {{ index + 1 }}:
              <div
                v-if="isReviewerRemoveable(index) && isMobile"
                class="tr__reviewers__reviewer__title__remove">
                <font-awesome-icon
                  :icon="['fal', 'times']"
                  size="1x"
                  @click="onRemoveReviewer(index)"></font-awesome-icon>
              </div>
            </div>
            <div class="tr__reviewers__reviewer__inputs">
              <div class="tr__reviewers__reviewer__inputs__field">
                <label for="firstName">First Name: <span class="asterix">*</span></label>
                <input
                  v-model="reviewers[index].firstName.value"
                  :class="{ invalid: reviewers[index].firstName.isValid === false }"
                  type="text"
                  :disabled="value.reviewerId"
                  @blur="validateAllPopulatedFields(index, 'firstName')">
                <b-alert
                  :show="reviewers[index].firstName.isValid === false"
                  variant="danger"
                  class="my-1 p-0 text-center">
                  {{ reviewers[index].firstName.errorMessage }}
                </b-alert>
              </div>
              <div class="tr__reviewers__reviewer__inputs__field">
                <label for="lastName">Second Name: <span class="asterix">*</span></label>
                <input
                  v-model="reviewers[index].lastName.value"
                  :class="{ invalid: reviewers[index].lastName.isValid === false }"
                  type="text"
                  :disabled="value.reviewerId"
                  @blur="validateAllPopulatedFields(index, 'lastName')">
                <b-alert
                  :show="reviewers[index].lastName.isValid === false"
                  variant="danger"
                  class="my-1 p-0 text-center">
                  {{ reviewers[index].lastName.errorMessage }}
                </b-alert>
              </div>
              <div class="tr__reviewers__reviewer__inputs__field">
                <label for="email">Email: <span class="asterix">*</span></label>
                <input
                  v-model="reviewers[index].email.value"
                  :class="{ invalid: reviewers[index].email.isValid === false }"
                  type="text"
                  @blur="validateAllPopulatedFields(index, 'email')">
                <b-alert
                  :show="reviewers[index].email.isValid === false"
                  variant="danger"
                  class="my-1 p-0 text-center">
                  {{ reviewers[index].email.errorMessage }}
                </b-alert>
              </div>
              <div
                v-if="reviewers[index].reviewerId"
                class="tr__reviewers__reviewer__inputs__field text-lg-center text-right">
                <label for="status">
                  Status:
                  <span
                    class="tr__reviewers__reviewer__inputs__field__status"
                    :class="ui.statusClass[reviewers[index].status]">
                    {{ ui.status[reviewers[index].status] }}
                  </span>
                </label>
                <b-form-checkbox
                  :id="`resend-email-confirmation-${index}`"
                  v-model="reviewers[index].resendEmailConfirmation"
                  name="`resend-email-confirmation-${index}`"
                  :value="true"
                  :unchecked-value="false">
                  resend email?
                </b-form-checkbox>
              </div>
              <div
                v-if="isMobile === false"
                class="tr__reviewers__reviewer__inputs__field__remove"
                :class="{
                  'tr__reviewers__reviewer__inputs__field__remove--hidden':
                    isReviewerRemoveable(index) === false,
                }">
                <font-awesome-icon
                  :icon="['fal', 'times']"
                  size="1x"
                  @click="onRemoveReviewer(index)"></font-awesome-icon>
              </div>
            </div>
          </div>
        </transition-group>
        <transition name="fade">
          <button
            v-if="reviewers.length < maxReviewers"
            class="cta cta--primary--alt"
            :class="{ 'd-none': reviewers.length === maxReviewers }"
            @click="addReviewer()">
            Add another reviewer
          </button>
        </transition>
      </template>
    </div>
    <div class="d-flex flex-wrap justify-content-between">
      <back-button>Back</back-button>
      <button class="cta cta--primary--alt"
              @click="onSaveReviewers">
        <template v-if="ui.save.loading">
          <loading-spinner size="1x"
                           :style="{ color: '#fff' }">
          </loading-spinner>
        </template>
        <template v-else>
          <font-awesome-icon :icon="['fal', 'save']"
                             size="1x"></font-awesome-icon>
          Save &amp; send
        </template>
      </button>
    </div>
    <b-alert :show="ui.save.error"
             variant="danger"
             class="alert my-2 text-center">
      {{ ui.save.errorMessage }}
    </b-alert>
  </div>
</template>
<script>
import BackButton from "~/Components/Common/BackButton.vue";

import {
  saveReviewers as saveThreeSixtyReviewers,
  listReviewers as listThreeSixtyReviewers,
  setReviewerEmailAsSent as setThreeSixtyReviewerEmailAsSent,
  listQuestions as listThreeSixtyQuestions,
  setInstanceAsSubmitted as setThreeSixtyInstanceAsSubmitted,
} from "~/Scripts/resources/threeSixtyResource";

import {
  saveReviewers as saveEngageReviewers,
  listReviewers as listEngageReviewers,
  setReviewerEmailAsSent as setEngageReviewerEmailAsSent,
  listQuestions as listEngageQuestions,
  setInstanceAsSubmitted as setEngageInstanceAsSubmitted,
} from "~/Scripts/resources/engageResource";

import {
  loadAnswers as listTeamStatements,
  saveMembers as saveTeamMembers,
  listMembers as listTeamMembers,
  setReviewAsComplete as setTeamMemberEmailAsSent,
  setInstanceAsSubmitted as setTeamInstanceAsSubmitted,
} from "~/Scripts/resources/teamResource";

import { REVIEWER_STATUS, INSTANCE_TYPE } from "~/Components/constants";

import { utils } from "~/Scripts/vue/mixins/common";

export default {
  name: "ToolReviewers",
  components: {
    BackButton,
  },
  mixins: [utils],
  props: {
    id: {
      type: String,
      default: null,
    },
    next: {
      type: String,
      required: true,
    },
    min: {
      type: Number,
      default: 3,
    },
    max: {
      type: Number,
      default: 999999,
    },
    instanceType: {
      type: String,
      required: true,
      validator: val => [
        INSTANCE_TYPE.THREE_SIXTY,
        INSTANCE_TYPE.PERSONA,
        INSTANCE_TYPE.STYLE,
        INSTANCE_TYPE.SURVEY,
        INSTANCE_TYPE.ENGAGE,
        INSTANCE_TYPE.TEAM,
      ].includes( val.toUpperCase() ),
    },
  },
  data: () => ( {
    REVIEWER_STATUS,
    teamAnswerKeys: ["areasOfStrength", "areasOfWeakness", "statementRanking"],
    windowWidth: 0,
    maxMobileWidth: 769,
    instanceId: null,
    reviewers: [],
    whitelistedKeys: ["reviewerId", "resendEmailConfirmation", "status"],
    ui: {
      save: {
        loading: false,
        error: false,
        errorMessage: "There was an issue saving your reviewers. Please, try again.",
      },
      load: {
        loading: false,
        error: false,
        errorMessage: "There was an issue loading your reviewers. Please, try again.",
      },
      status: {
        NOT_STARTED: "Not started",
        STARTED: "Started",
        COMPLETED: "Completed",
        UNKNOWN: "Unknown",
      },
      statusClass: {
        NOT_STARTED: "tr__reviewers__reviewer__inputs__field__status--not-started",
        STARTED: "tr__reviewers__reviewer__inputs__field__status--started",
        COMPLETED: "tr__reviewers__reviewer__inputs__field__status--completed",
        UNKNOWN: "tr__reviewers__reviewer__inputs__field__status--unknown",
      },
    },
  } ),
  computed: {
    isFirstSubmission () {
      return this.reviewers.find( r => r.reviewerId ) === undefined;
    },
    isMobile () {
      return this.windowWidth < this.maxMobileWidth && window.innerWidth < this.maxMobileWidth;
    },
    listInstanceQuestions () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.THREE_SIXTY: {
          return listThreeSixtyQuestions;
        }
        case INSTANCE_TYPE.ENGAGE: {
          return listEngageQuestions;
        }
        case INSTANCE_TYPE.TEAM: {
          return listTeamStatements;
        }
        default: {
          return null;
        }
      }
    },
    setInstanceAsSubmitted () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.THREE_SIXTY: {
          return setThreeSixtyInstanceAsSubmitted;
        }
        case INSTANCE_TYPE.ENGAGE: {
          return setEngageInstanceAsSubmitted;
        }
        case INSTANCE_TYPE.TEAM: {
          return setTeamInstanceAsSubmitted;
        }
        default: {
          return null;
        }
      }
    },
    listReviewers () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.THREE_SIXTY: {
          return listThreeSixtyReviewers;
        }
        case INSTANCE_TYPE.ENGAGE: {
          return listEngageReviewers;
        }
        case INSTANCE_TYPE.TEAM: {
          return listTeamMembers;
        }
        default: {
          return null;
        }
      }
    },
    setReviewerEmailAsSent () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.THREE_SIXTY: {
          return setThreeSixtyReviewerEmailAsSent;
        }
        case INSTANCE_TYPE.ENGAGE: {
          return setEngageReviewerEmailAsSent;
        }
        case INSTANCE_TYPE.TEAM: {
          return setTeamMemberEmailAsSent;
        }
        default: {
          return null;
        }
      }
    },
    saveReviewers () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.THREE_SIXTY: {
          return this.saveThreeSixtyReviewers();
        }
        case INSTANCE_TYPE.ENGAGE: {
          return this.saveEngageReviewers();
        }
        case INSTANCE_TYPE.TEAM: {
          return this.saveTeamMembers();
        }
        default:
          return null;
      }
    },
    maxReviewers () {
      let result = 999999;

      if ( this.max > 0 ) {
        result = this.max;
      }
      return result;
    },
  },
  created () {
    const params = this.getURLParams();
    const id = this.id || params.id;

    if ( id ) {
      this.instanceId = id;
    } else {
      console.error( "We have encountered an issue", id );
      window.location.href = "/error";
    }

    this.loadReviewers();
  },
  mounted () {
    window.addEventListener( "resize", this.setScreenWidth );
  },
  unmounted () {
    window.removeEventListener( "resize", this.setScreenWidth );
  },
  methods: {
    async addReviewer ( firstName = "", lastName = "", email = "", id = null ) {
      if ( this.reviewers.length === this.maxReviewers ) {
        return;
      }

      const reviewer = {
        firstName: {
          value: firstName,
          isValid: true,
          errorMessage: undefined,
        },
        lastName: {
          value: lastName,
          isValid: true,
          errorMessage: undefined,
        },
        email: {
          value: email,
          isValid: true,
          errorMessage: undefined,
        },
        resendEmailConfirmation: false,
      };

      if ( id && this.listInstanceQuestions ) {
        reviewer.reviewerId = id;

        const { data: questions } = await this.listInstanceQuestions( this.instanceId, id );

        if ( questions && Array.isArray( questions ) && questions.length > 0 ) {
          const remainingQuestions = questions.filter( q => q.answer == null );

          if ( remainingQuestions.length === 0 ) {
            reviewer.status = REVIEWER_STATUS.COMPLETED;
          } else if ( remainingQuestions.length < questions.length ) {
            reviewer.status = REVIEWER_STATUS.STARTED;
          } else {
            reviewer.status = REVIEWER_STATUS.NOT_STARTED;
          }
        } else if ( this.teamAnswerKeys.every( key => key in questions ) ) {
          const { statementRanking, areasOfStrength, areasOfWeakness } = questions;

          const hasStrengths = this.isNullEmptyOrIncomplete( areasOfStrength ) === false;
          const hasWeaknesses = this.isNullEmptyOrIncomplete( areasOfWeakness ) === false;
          const hasRanking = this.isNullEmptyOrIncomplete( statementRanking ) === false;

          if ( hasStrengths && hasWeaknesses && hasRanking ) {
            reviewer.status = REVIEWER_STATUS.COMPLETED;
          } else if ( hasStrengths || hasWeaknesses || hasRanking ) {
            reviewer.status = REVIEWER_STATUS.STARTED;
          } else {
            reviewer.status = REVIEWER_STATUS.NOT_STARTED;
          }
        } else {
          reviewer.status = REVIEWER_STATUS.UNKNOWN;
        }
      }

      this.reviewers.push( { ...reviewer } );
    },
    setScreenWidth () {
      this.windowWidth = window.innerWidth;
    },
    isNullEmptyOrIncomplete ( val ) {
      if ( val == null ) {
        return true;
      }
      if ( Array.isArray( val ) || typeof val === "string" ) {
        return val.length === 0;
      }
      if ( typeof val === "object" ) {
        return !Object.values( val ).some( x => x != null && x !== "" );
      }
      return true;
    },
    async onSaveReviewers () {
      this.ui.save.loading = true;

      if ( this.validate() ) {
        try {
          const reviewers = await this.saveReviewers;
          await this.sendEmailsToSelectedReviewers( reviewers );
          if ( this.isFirstSubmission ) {
            await this.submitInstance();
          }
          window.location.href = `${this.next}?id=${this.instanceId}`;
        } catch ( err ) {
          console.error( err );
          this.ui.save.error = true;
        } finally {
          this.ui.save.loading = false;
        }
      } else {
        this.ui.save.loading = false;
        console.warn( "Form is not valid." );
      }
    },
    onRemoveReviewer ( index ) {
      const mutableReviewers = [...this.reviewers];
      mutableReviewers.splice( index, 1 );
      this.reviewers = mutableReviewers;
    },
    isReviewerRemoveable ( index ) {
      return this.reviewers[index].reviewerId == null && index + 1 > this.min;
    },
    loadReviewers () {
      this.ui.load.loading = true;

      if ( this.listReviewers ) {
        this.listReviewers( this.instanceId )
          .then( response => {
            const { status, data } = response;
            if ( status === 200 && data ) {
              if ( data.length > 0 ) {
                data.forEach( r => {
                  // accomadate for 360, Engage and Team
                  const reviewerId = r.reviewerId || r.teamMemberId;
                  this.addReviewer( r.firstName, r.lastName, r.emailAddress, reviewerId );
                } );
              } else {
                this.createDefaultReviewers();
              }
            } else {
              this.createDefaultReviewers();
              throw new Error( "Failed to load reviewers" );
            }
          } )
          .catch( ex => {
            console.error( ex );
            this.ui.load.error = true;
          } )
          .finally( () => {
            this.ui.load.loading = false;
          } );
      } else {
        this.createDefaultReviewers();
      }
    },
    createDefaultReviewers () {
      for ( let i = 0; i < this.min; i += 1 ) {
        this.addReviewer();
      }
    },
    saveThreeSixtyReviewers () {
      return new Promise( ( resolve, reject ) => {
        const mappedReviewers = this.reviewers.map( r => ( {
          ...r,
          firstName: r.firstName.value,
          lastName: r.lastName.value,
          emailAddress: r.email.value,
        } ) );

        const payload = {
          instanceId: this.instanceId,
          reviewers: mappedReviewers,
        };

        saveThreeSixtyReviewers( payload )
          .then( async response => {
            const { status, data: reviewers } = response;
            if ( status === 200 && reviewers ) {
              resolve( reviewers );
            } else {
              reject( new Error( "Failed to save reviewers" ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    saveEngageReviewers () {
      return new Promise( ( resolve, reject ) => {
        const mappedReviewers = this.reviewers.map( r => ( {
          ...r,
          firstName: r.firstName.value,
          lastName: r.lastName.value,
          emailAddress: r.email.value,
        } ) );

        const payload = {
          instanceId: this.instanceId,
          reviewers: mappedReviewers,
        };

        saveEngageReviewers( payload )
          .then( async response => {
            const { status, data: reviewers } = response;
            if ( status === 200 && reviewers ) {
              resolve( reviewers );
            } else {
              reject( new Error( "Failed to save reviewers" ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    saveTeamMembers () {
      return new Promise( ( resolve, reject ) => {
        const mappedMembers = this.reviewers.map( r => ( {
          ...r,
          firstName: r.firstName.value,
          lastName: r.lastName.value,
          emailAddress: r.email.value,
        } ) );

        const payload = {
          instanceId: this.instanceId,
          members: mappedMembers,
        };

        saveTeamMembers( payload )
          .then( async response => {
            const { status, data: members } = response;
            if ( status === 200 && members ) {
              resolve( members );
            } else {
              reject( new Error( "Failed to save members" ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    getEmailValidations ( email ) {
      const lowerCaseEmail = String( email ).toLowerCase();
      // eslint-disable-next-line
      const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return {
        isValid: emailRegex.test( lowerCaseEmail ),
        isUnique:
          // eslint-disable-next-line
          this.reviewers.filter(r => r.email?.value?.toLowerCase() === lowerCaseEmail).length <= 1,
      };
    },
    validateAllPopulatedFields () {
      this.reviewers.forEach( ( r, i ) => {
        Object.keys( r ).forEach( k => {
          if ( r[k] && r[k].value ) {
            this.validateField( i, k );
          }
        } );
      } );
    },
    validateFieldsOfType ( index, key ) {
      this.reviewers.forEach( ( r, i ) => {
        if ( r[key] && r[key].value ) {
          this.validateField( i, key );
        }
      } );
    },
    validateField ( index, key ) {
      const mutableReviewer = { ...this.reviewers[index] };

      if ( this.whitelistedKeys.includes( key ) ) {
        return true;
      }

      // check field has value
      if ( mutableReviewer[key].value == null || mutableReviewer[key].value === "" ) {
        mutableReviewer[key].isValid = false;
        mutableReviewer[key].errorMessage = "This field is required";
        this.reviewers[index] = mutableReviewer;
        return false;
      }

      // check email is valid
      const { isValid, isUnique } = this.getEmailValidations( mutableReviewer[key].value );
      if ( key === "email" && ( !isValid || !isUnique ) ) {
        mutableReviewer[key].isValid = false;

        if ( !isValid ) {
          mutableReviewer[key].errorMessage = "Please enter a valid email";
        }
        if ( !isUnique ) {
          mutableReviewer[key].errorMessage = "This email is already in use";
        }
        this.reviewers[index] = mutableReviewer;
        return false;
      }

      // valid
      mutableReviewer[key].isValid = true;
      this.reviewers[index] = mutableReviewer;
      return true;
    },
    validate () {
      let isValid = true;

      const updatedReviewers = this.reviewers.map( ( reviewer, index ) => {
        const mutableReviewer = { ...reviewer };

        Object.keys( mutableReviewer ).forEach( key => {
          if ( this.validateField( index, key ) === false ) {
            isValid = false;
          }
        } );

        return mutableReviewer;
      } );

      this.reviewers = updatedReviewers;

      return isValid;
    },
    sendEmailsToSelectedReviewers ( instanceReviewers ) {
      const reviewersEmailAsSentPromises = instanceReviewers
        .map( r => {
          const { reviewerId } = r;
          const knownReviewer = this.reviewers.find( r2 => r2.reviewerId === reviewerId );
          /*
          Known reviewers are retrieved from the database and thier respective ID is loaded.
          Otherwise this value is undefined and should be treated as a new reviewer.
          Emails are automatically sent for new reviewers when they are inserted (saved)
          in the database.
          */
          if ( knownReviewer && knownReviewer.resendEmailConfirmation === true ) {
            return this.setReviewerEmailAsSent
              ? this.setReviewerEmailAsSent( {
                reviewerId,
              } )
              : null;
          }
          return null;
        } )
        .filter( promise => promise != null );

      Promise.all( reviewersEmailAsSentPromises ).then( responses => {
        console.info( responses );
      } );
    },
    async submitInstance () {
      try {
        this.$emit( "loading", true );
        await this.setInstanceAsSubmitted( { instanceId: this.instanceId } );
        this.$emit( "submitted" );
      } catch ( err ) {
        console.error( err );
        this.$emit( "error", err );
      } finally {
        this.$emit( "loading", false );
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../Styles/utils/index.less";

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.3s;
}

.asterix {
  color: red;
}

.tr {
  position: relative;
  border: 1px solid @blue;
  padding: 16px;
  .border-radius(4px);

  &__container {
    display: flex;
    flex-flow: column;
    gap: 32px;
    margin: 0 0 32px;
  }

  &__background {
    .opacity-background(@light-blue, 0.9);
  }

  &__title {
    color: @blue;
    font-weight: 700;
    font-size: 18px;
  }

  &__reviewers {
    &__reviewer {
      margin: 0 0 24px;

      &__title {
        font-size: 18px;
        font-weight: 700;
        display: flex;
        justify-content: space-between;

        &__remove {
          color: @blue;
          cursor: pointer;
        }
      }

      &__inputs {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;

        &__field {
          display: flex;
          flex-flow: column;
          flex: 1;

          @media (max-width: @md) {
            flex: 0 1 auto;
            width: 100%;
          }

          &__status {
            font-weight: 700;

            &--unknown {
              color: #333;
            }

            &--not-started {
              color: #333;
            }

            &--started {
              color: @started;
            }

            &--completed {
              color: @mastered;
            }
          }

          &__remove {
            color: @blue;
            height: 1px;
            cursor: pointer;
            flex: 0 1 auto;
            margin: auto;

            &--hidden {
              visibility: hidden;
            }
          }

          > label {
            margin: 0;
          }

          > input {
            border: 1px solid @blue;
            background: #fff;
            height: 40px;

            &.invalid {
              border: 1px solid red;
            }
          }
        }
      }
    }
  }
}
</style>
